import { withTheme } from 'foundations-theming/utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import Raven from 'raven-js';
import Immutable from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { H7 } from 'foundations-components/transitional/components/H7';
import { FileButton } from 'foundations-components/transitional/components/FileButton';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { Small } from 'foundations-components/transitional/components/Small';
import { uploadFileV3 } from 'FileManagerCore/api/Files';
import { getUploadErrorType } from 'FileManagerCore/utils/errors';
import { serializeFileInfo } from 'FileManagerCore/utils/logging';
import { FileExtensionFilterTypes } from 'file-manager-components/constants';
import { pickValidUploadV3Options } from '../../utils/fileUploads';
import { getIsDropZoneNoBrowseFileAccessValid } from '../../utils/validateFileAccess';
import { UploadStatus } from '../../Constants';
import FileUploadComponentSizes from '../../enums/FileUploadComponentSize';
import { FileExtensionFilterEnum } from '../../enums/FileExtensionFilters';
import DropZoneIconSize from '../../enums/DropZoneIconSize';
import { getFileDataUploadStatus } from '../../utils/getFileDataUploadStatus';
import DropZoneErrorTypes from '../../enums/DropZoneErrorTypes';
import { isExtensionFromRawFileSupported, getFileExtensionFromRawFile, buildFilterFromPOJOs } from '../../utils/fileFiltering';
// @ts-ignore not yet typed
import FileDropZoneUploadError from '../errors/FileDropZoneUploadError';
import DropTargetZone from './DropTargetZone';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function FileUploadComponent({
  icon,
  size = FileUploadComponentSizes.large,
  iconSize = DropZoneIconSize.large,
  showSpinnerProgress = true,
  showNanoProgress = true,
  showFileData = true,
  showFileName = true,
  use = 'default',
  children,
  uploadButtonRenderer,
  _httpClient,
  fileData,
  onChange,
  closeable,
  onClose,
  fileUploadOptions = {},
  uploadedFileAccess,
  onFileAdd,
  filteredExtensions = [],
  filteredReasons = {},
  filterType = FileExtensionFilterEnum.NONE,
  theme
}) {
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(getFileDataUploadStatus(fileData));
  const [fileInfo, setFileInfo] = useState(fileData ? fileData.toObject() : null);
  const [error, setError] = useState(null);
  const xhrRef = useRef(null);
  const currentFileRef = useRef(null);
  const shouldRenderSpinnerProgress = showSpinnerProgress && status === UploadStatus.PROCESSING;
  const shouldRenderNanoProgress = showNanoProgress && status === UploadStatus.PROCESSING;
  const renderedIconSize = iconSize || DropZoneIconSize[size];
  const getStatus = () => {
    if (status === UploadStatus.PROCESSING) {
      return 'processing';
    }
    if (status === UploadStatus.FAILED || error) {
      return 'error';
    }
    if (status === UploadStatus.FINISHED) {
      return use || 'success';
    }
    return use || 'default';
  };
  const shouldRenderFileData = showFileData && Boolean(fileInfo) && getStatus() !== 'error';
  const showIcon = getStatus() !== 'processing';
  useEffect(() => {
    if (!getIsDropZoneNoBrowseFileAccessValid(uploadedFileAccess)) {
      throw new Error('[FileManagerLib/FileUploadComponent] Missing uploadedFileAccess prop. Visit https://product.hubteam.com/docs/file-manager-manual/Frontend/index.html for details.');
    }
  }, [uploadedFileAccess]);
  const logPageAction = useCallback((eventName, extra = {}) => {
    Raven.capturePageEvent(eventName, {
      extra: Object.assign({}, extra, {
        componentName: 'FileUploadComponent',
        uploadedFileAccess
      })
    });
  }, [uploadedFileAccess]);
  useEffect(() => {
    logPageAction('mount', {
      isFiltering: Boolean(filterType)
    });
  }, [logPageAction, filterType]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatus(getFileDataUploadStatus(fileData));
    setFileInfo(fileData ? fileData.toObject() : null);
  }, [fileData]);
  const setXhr = useCallback(xhr => {
    xhrRef.current = xhr;
  }, []);
  const handleProgress = useCallback(event => {
    const updatedProgress = event.loaded / event.total * 100;
    setProgress(updatedProgress);
  }, []);
  const abortCurrentRequest = useCallback(() => {
    if (xhrRef.current && xhrRef.current.readyState !== 4) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setProgress(null);
    setStatus(null);
    setFileInfo(null);
    onChange(null);
  }, [onChange]);
  const handleUpload = useCallback((files, {
    target = 'upload-button'
  } = {}) => {
    setError(null);
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];
    abortCurrentRequest();
    const filter = buildFilterFromPOJOs(filterType, filteredExtensions, filteredReasons);
    if (filter.get('filterType') !== FileExtensionFilterTypes.NONE && !isExtensionFromRawFileSupported(file, filter)) {
      setError({
        type: DropZoneErrorTypes.INVALID_FILE_EXTENSION,
        fileExtension: getFileExtensionFromRawFile(file),
        filter
      });
      return;
    }
    setProgress(0);
    setStatus(UploadStatus.PROCESSING);
    setFileInfo(file);
    currentFileRef.current = file;
    if (typeof onFileAdd === 'function') {
      onFileAdd(file);
    }
    uploadFileV3(file, uploadedFileAccess, pickValidUploadV3Options(fileUploadOptions), {
      updateProgress: handleProgress,
      xhrCallback: setXhr,
      httpClient: _httpClient
    }).then(data => {
      const fileDataObj = data.getIn(['objects', 0]);
      const url = fileDataObj.get('url');
      setProgress(null);
      setStatus(UploadStatus.FINISHED);
      onChange(url, fileDataObj.set('file', file), {
        target
      });
      logPageAction('uploadSuccess', Object.assign({}, serializeFileInfo(file), {
        target
      }));
    }).catch(err => {
      const errorType = getUploadErrorType(err);
      logPageAction('uploadFailed', Object.assign({}, serializeFileInfo(file), {
        errorType,
        target
      }));
      if (currentFileRef.current === file) {
        setProgress(null);
        setStatus(UploadStatus.FAILED);
        setError({
          type: errorType
        });
        const errorData = Immutable.fromJS({
          file,
          data: err.data,
          message: err.message,
          responseJSON: err.responseJSON,
          responseText: err.responseText,
          status: err.status,
          statusText: err.statusText
        });
        onChange(null, errorData, {}, undefined);
      }
    });
  }, [abortCurrentRequest, filteredExtensions, filteredReasons, filterType, fileUploadOptions, onChange, onFileAdd, uploadedFileAccess, _httpClient, setXhr, handleProgress, logPageAction]);
  const handleUploadClick = useCallback(event => {
    if (event.target.files) {
      handleUpload([...event.target.files]);
    }
  }, [handleUpload]);
  const handleRemove = useCallback(() => {
    abortCurrentRequest();
    if (onClose) {
      onClose();
    }
  }, [abortCurrentRequest, onClose]);
  const getIcon = () => {
    if (error) {
      return WarningIcon;
    }
    return icon || undefined;
  };
  const renderUploadButton = () => {
    if (uploadButtonRenderer) {
      return uploadButtonRenderer(handleUploadClick);
    }
    return /*#__PURE__*/_jsx(FileButton, {
      size: "small",
      onChange: handleUploadClick,
      "data-test-id": "file-upload-button",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "FileManagerCore.actions.upload"
      })
    });
  };
  const renderActions = () => {
    if (children) {
      return children;
    }
    if (status === UploadStatus.PROCESSING) {
      return null;
    }
    return renderUploadButton();
  };
  const renderFileName = () => {
    const fileSize = fileInfo && fileInfo.size;
    if (getStatus() === 'error' && typeof fileSize !== 'number') {
      return null;
    }
    if (fileInfo) {
      const originalFileName = fileInfo.file ? fileInfo.file.name : fileInfo.name;
      const uploadedFileName = fileData ? `${fileData.get('name')}.${fileData.get('extension')}` : originalFileName;
      return /*#__PURE__*/_jsx(H7, {
        marginBlockEnd: "0",
        "data-test-id": "uploaded-file-name",
        children: uploadedFileName.toLowerCase() !== originalFileName.toLowerCase() ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerLib.fileUploadRenamed",
          options: {
            fileName: uploadedFileName
          }
        }) : originalFileName
      });
    }
    return null;
  };
  const renderFileData = () => {
    const fileSize = fileInfo && fileInfo.size;
    if (!fileInfo || !fileSize) {
      return null;
    }
    if (status === UploadStatus.PROCESSING) {
      const progressString = I18n.formatSize(fileSize * (progress / 100)).toUpperCase();
      const totalString = I18n.formatSize(fileInfo.size).toUpperCase();
      return /*#__PURE__*/_jsx(Small, {
        minInlineSize: "120px",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "FileManagerLib.percentDownloadOf",
          options: {
            part: progressString,
            total: totalString
          }
        })
      });
    }
    return /*#__PURE__*/_jsx(Small, {
      children: I18n.formatSize(fileSize).toUpperCase()
    });
  };
  const renderCloseButton = () => {
    if (fileInfo && closeable) {
      return /*#__PURE__*/_jsx(CloseButton, {
        onClick: handleRemove,
        color: "do-not-use-eerie",
        size: "small",
        insetBlockStart: "14px",
        insetInlineEnd: "18px"
      });
    }
    return null;
  };
  const renderSmallDropZone = () => {
    return /*#__PURE__*/_jsxs(DropTargetZone, {
      "data-status": `status-${getStatus()}`,
      icon: showIcon ? getIcon() : undefined,
      iconSize: renderedIconSize,
      use: getStatus(),
      size: size,
      onUpload: handleUpload,
      children: [shouldRenderNanoProgress && /*#__PURE__*/_jsx(NanoProgress, {
        value: progress,
        boxShadow: false,
        color: theme === null || theme === void 0 ? void 0 : theme.color['do-not-use-calypso']
      }), /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        children: [shouldRenderSpinnerProgress && /*#__PURE__*/_jsx(LoadingSpinner, {
          layout: "centered",
          size: "extra-small",
          use: "success"
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "center",
          children: [showFileName && renderFileName(), shouldRenderFileData && renderFileData(), renderActions(), error && /*#__PURE__*/_jsx(FileDropZoneUploadError, Object.assign({}, error))]
        })]
      }), renderCloseButton()]
    });
  };
  function renderBigDropZone() {
    return /*#__PURE__*/_jsxs(DropTargetZone, {
      "data-status": `status-${getStatus()}`,
      icon: showIcon ? getIcon() : undefined,
      use: getStatus(),
      size: size,
      onUpload: handleUpload,
      children: [shouldRenderNanoProgress && /*#__PURE__*/_jsx(NanoProgress, {
        value: progress,
        boxShadow: false,
        color: theme === null || theme === void 0 ? void 0 : theme.color['do-not-use-calypso']
      }), shouldRenderSpinnerProgress && /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered",
        size: "small",
        use: "success"
      }), showFileName && renderFileName(), error && /*#__PURE__*/_jsx(FileDropZoneUploadError, Object.assign({}, error)), shouldRenderFileData && renderFileData(), renderActions(), renderCloseButton()]
    });
  }
  return size === FileUploadComponentSizes.small ?
  // eslint-disable-next-line react-hooks/refs
  renderSmallDropZone() :
  // eslint-disable-next-line react-hooks/refs
  renderBigDropZone();
}
export default withTheme(FileUploadComponent);